import React, { useState } from "react";
import { Save, Download, Trash2, AlertTriangle, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { extrasApi, bl } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function Settings() {
    const { locale, setLocale } = useI18n();
    const { user, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState(user?.name || "");
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [dpaLoading, setDpaLoading] = useState(false);

    async function saveProfile() {
        setSaving(true);
        try {
            await extrasApi.updateMe({ name, locale });
            toast.success(bl("Profile saved", "Profil sauvegardé", locale));
            refresh();
        } catch (_e) { toast.error("Failed"); }
        finally { setSaving(false); }
    }

    async function exportData() {
        setExporting(true);
        try {
            const data = await extrasApi.rgpdExport();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `boxeb2b-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(bl("Export downloaded", "Export téléchargé", locale));
        } catch (_e) { toast.error("Failed"); }
        finally { setExporting(false); }
    }

    async function deleteAccount() {
        const confirm1 = window.prompt(bl(
            'This will PERMANENTLY delete your account, workspace, all leads, campaigns and data. Type "DELETE" to confirm.',
            'Cela va SUPPRIMER définitivement ton compte, workspace, leads, campagnes et data. Tape "DELETE" pour confirmer.',
            locale,
        ));
        if (confirm1 !== "DELETE") return;
        setDeleting(true);
        try {
            await extrasApi.rgpdDelete();
            toast.success(bl("Account deleted", "Compte supprimé", locale));
            await logout();
            navigate("/");
        } catch (_e) { toast.error("Failed"); setDeleting(false); }
    }

    async function downloadDpa() {
        setDpaLoading(true);
        try {
            await extrasApi.downloadDpa();
            toast.success(bl("DPA downloaded", "DPA téléchargé", locale));
        } catch (_e) { toast.error("Failed"); }
        finally { setDpaLoading(false); }
    }

    return (
        <div className="px-6 lg:px-10 py-6 max-w-3xl">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{bl("Settings", "Paramètres", locale)}</h1>
                <p className="mt-1 text-sm text-zinc-500">{bl("Profile, locale, GDPR data export & deletion.", "Profil, langue, export & suppression RGPD.", locale)}</p>
            </div>

            <div className="rounded-2xl p-6 bg-brand-surface border border-white/8 mb-6">
                <div className="text-sm font-semibold mb-4">{bl("Profile", "Profil", locale)}</div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-zinc-400">Email</label>
                        <input value={user?.email || ""} disabled className="mt-1 w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm text-zinc-500" />
                    </div>
                    <div>
                        <label className="text-xs text-zinc-400">{bl("Full name", "Nom complet", locale)}</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" className="mt-1 w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo" />
                    </div>
                    <div>
                        <label className="text-xs text-zinc-400">{bl("Language", "Langue", locale)}</label>
                        <div className="mt-1 flex gap-2">
                            <button type="button" onClick={() => setLocale("en")} data-testid="settings-locale-en" className={`px-4 py-2 rounded-lg text-sm ${locale === "en" ? "bg-brand-indigo text-white" : "bg-brand-void border border-white/10 text-zinc-300"}`}>English</button>
                            <button type="button" onClick={() => setLocale("fr")} data-testid="settings-locale-fr" className={`px-4 py-2 rounded-lg text-sm ${locale === "fr" ? "bg-brand-indigo text-white" : "bg-brand-void border border-white/10 text-zinc-300"}`}>Français</button>
                        </div>
                    </div>
                </div>
                <button type="button" onClick={saveProfile} disabled={saving} data-testid="settings-save" className="mt-5 btn-cta text-white text-sm font-semibold px-5 py-2 rounded-full inline-flex items-center gap-2">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {bl("Save", "Sauvegarder", locale)}
                </button>
            </div>

            <div className="rounded-2xl p-6 bg-brand-surface border border-white/8 mb-6">
                <div className="text-sm font-semibold mb-2">{bl("GDPR / Data export", "RGPD / Export data", locale)}</div>
                <p className="text-xs text-zinc-500 mb-4">{bl("Download every lead, campaign, event and setting in a single JSON.", "Télécharge tous tes leads, campagnes, événements et paramètres en un seul JSON.", locale)}</p>
                <button type="button" onClick={exportData} disabled={exporting} data-testid="settings-export" className="text-white text-sm font-medium px-4 py-2 rounded-full border border-white/12 hover:border-brand-cyan/50 hover:bg-brand-cyan/5 inline-flex items-center gap-2">
                    {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} {bl("Export all my data", "Exporter mes données", locale)}
                </button>
            </div>

            <div className="rounded-2xl p-6 bg-brand-surface border border-white/8 mb-6">
                <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <FileText size={14} className="text-brand-cyan" />
                    {bl("Data Processing Agreement (DPA)", "Accord de Traitement des Données (DPA)", locale)}
                </div>
                <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
                    {bl(
                        "RGPD Art. 28-compliant DPA pre-filled with your organization details. Required by most enterprise buyers before signing. Pre-signed by BOXEB2B — just counter-sign it.",
                        "DPA conforme RGPD Art. 28 pré-rempli avec les infos de ton organisation. Requis par la plupart des acheteurs enterprise avant signature. Pré-signé par BOXEB2B — il te suffit de le contresigner.",
                        locale,
                    )}
                </p>
                <button
                    type="button"
                    onClick={downloadDpa}
                    disabled={dpaLoading}
                    data-testid="settings-download-dpa"
                    className="text-white text-sm font-medium px-4 py-2 rounded-full border border-brand-indigo/40 bg-brand-indigo/10 hover:bg-brand-indigo/20 inline-flex items-center gap-2"
                >
                    {dpaLoading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                    {bl("Download DPA (PDF)", "Télécharger le DPA (PDF)", locale)}
                </button>
            </div>

            <div className="rounded-2xl p-6 bg-rose-500/5 border border-rose-500/25">
                <div className="flex items-center gap-2 text-sm font-semibold text-rose-200 mb-2">
                    <AlertTriangle size={16} /> {bl("Danger zone", "Zone dangereuse", locale)}
                </div>
                <p className="text-xs text-zinc-400 mb-4">{bl("Permanently delete your account, workspace and every lead/campaign/event. Irreversible.", "Supprime définitivement compte, workspace et toute la data. Irréversible.", locale)}</p>
                <button type="button" onClick={deleteAccount} disabled={deleting} data-testid="settings-delete" className="text-sm font-semibold px-4 py-2 rounded-full border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-2">
                    {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} {bl("Delete my account", "Supprimer mon compte", locale)}
                </button>
            </div>
        </div>
    );
}
